import {
    createSelector,
    createStructuredSelector,
} from 'reselect';
import {
    filterTechnicalDetails,
    getFromArrayWithRunTimeEnd,
    getMonthlyPriceWithOrWithoutDiscounts,
    PriceType,
} from '@vfde-sails/glados-v2';
import {
    ADDITIONAL_PAGE_OPTIONS,
    STONE_VIRTUAL_ID,
} from '@vfde-sails/constants';
import { RootState } from '../../store';
import {
    selectCurrentColor,
    selectCustomerConfig,
    selectCurrentCaseSize,
    selectActiveAccordionPanel,
} from './slice';
import {
    getDeviceDetailGroup,
    getTariffWithHardware,
} from 'Container/App/api/glados';

const createAppSelector = createSelector.withTypes<RootState>();

const getSelectorForDeviceDetailGroupQuery = createAppSelector(
    selectCustomerConfig,
    ({ deviceId }) =>
        getDeviceDetailGroup.select(deviceId),
);

const selectDeviceDetailGroupQuery = createAppSelector(
    [
        state => state,
        getSelectorForDeviceDetailGroupQuery,
    ],
    (state, selector) => selector(state),
);

const selectDevice =
    createAppSelector(
        selectDeviceDetailGroupQuery,
        query => query.data || null,
    );

const selectCellular = createAppSelector(
    selectDevice,
    device => device?.connectivity || null,
);

const selectColorOptions = createAppSelector(
    selectDevice,
    device => {
        if (!device) {
            return [];
        }

        return device.availableColors;
    },
);

const selectCaseSizeOptions = createAppSelector(
    selectDevice,
    device => {
        if (!device || !device.availableCaseSizes) {
            return [];
        }

        return device.availableCaseSizes.map(caseSize => {
            const { atomics } = device;
            // TODO: move to client
            const atomicForPrice = atomics.find(atomic => atomic.caseSize === caseSize)!;

            return {
                displayLabel: caseSize,
                sortOrder: parseInt(caseSize.match(/\d+/)![0], 10),
                hardwareWithTariffPrice: atomicForPrice.priceHardwareOneTimeWithTariff,
                hardwareOnlyPrice: atomicForPrice.priceHardwareOnly,
            };
        }).toSorted((sizeA, sizeB) => sizeA.sortOrder - sizeB.sortOrder);
    },
);

const selectCaseSizesForColor = createAppSelector(
    selectDevice,
    selectCurrentColor,
    (device, currentColor) => {
        if (!device) {
            return [];
        }

        if (!currentColor) {
            return device.availableCaseSizes || [];
        }

        const items: string[] = [];

        for (const atomic of device.atomics) {
            if (currentColor === atomic.color.displayLabel) {
                items.push(atomic.caseSize!);
            }
        }

        return [...new Set(items)];
    },
);

const selectAtomicDevices =
    createAppSelector(
        selectDevice,
        device => device?.atomics || [],
    );

const selectAtomicDevice = createAppSelector(
    [
        selectCustomerConfig,
        selectAtomicDevices,
    ],
    (customerConfig, atomics) => {
        if (!atomics.length) {
            return null;
        }

        const { atomicId } = customerConfig;

        return atomics
            .find(atomic => atomicId ? atomic.atomicId === atomicId : atomic.isDefault) || null;
    },
);

const selectHardwareOneTimePrice = createAppSelector(
    selectAtomicDevice,
    selectCustomerConfig,
    (atomicDevice, customerConfig) => {
        if (!atomicDevice) {
            return null;
        }

        return customerConfig?.isHardwareOnly || customerConfig.paymentMethod === PriceType.Onetime
            ? atomicDevice.priceHardwareOnly
            : atomicDevice.priceHardwareOneTimeWithTariff;
    },
);

const selectAtomicImages = createAppSelector(
    selectAtomicDevice,
    atomic => atomic?.imageUrls || [],
);

const getSelectorForTariffWithHardwareQuery = createAppSelector(
    selectAtomicDevices,
    atomics => getTariffWithHardware.select(atomics.map(atomic => atomic.atomicId)),
);

const selectTariffWithHardwareQuery = createAppSelector(
    [
        state => state,
        getSelectorForTariffWithHardwareQuery,
    ],
    (state, selector) => selector(state),
);

const selectLoading = createAppSelector(
    [
        selectDeviceDetailGroupQuery,
        selectTariffWithHardwareQuery,
    ],
    (deviceDetailGroupQuery, tariffWithHardwareQuery) => deviceDetailGroupQuery.isLoading || tariffWithHardwareQuery.isLoading,
);

const selectHasError = createAppSelector(
    [
        selectDeviceDetailGroupQuery,
        selectTariffWithHardwareQuery,
    ],
    (deviceDetailGroupQuery, tariffWithHardwareQuery) => deviceDetailGroupQuery.isError || tariffWithHardwareQuery.isError,
);

const selectSubscription = createAppSelector(
    selectCustomerConfig,
    selectTariffWithHardwareQuery,
    (customerConfig, query) =>
        query.data
            ?.find(tariffData => tariffData.hardware.atomicId === customerConfig.atomicId)?.tariffs
            .find(tariff => tariff.tariffId === STONE_VIRTUAL_ID) || null,
);

const selectTariffPrice = createAppSelector(
    selectCustomerConfig,
    selectSubscription,
    (customerConfig, subscription) => {
        if (!subscription) {
            return null;
        }

        return customerConfig.paymentMethod === PriceType.Onetime
            ? {
                [PriceType.Monthly]: {
                    withoutDiscounts: subscription.priceMultiSimMonthly!,
                },
                [PriceType.Onetime]: {
                    withoutDiscounts: subscription.priceMultiSimConnectionFee,
                },
            }
            : {
                [PriceType.Monthly]: {
                    withDiscounts: subscription.priceMonthlyTariffWithDiscounts,
                    withoutDiscounts: subscription.priceMonthlyTariffWithoutDiscounts,
                },
                [PriceType.Onetime]: {
                    withoutDiscounts: subscription.priceConnectionFee,
                },
            };
    },
);

const selectMonthlyTariffPrice = createAppSelector(
    selectTariffPrice,
    tariffPrice => {
        if (!tariffPrice) {
            return null;
        }

        const monthlyPrice = tariffPrice[PriceType.Monthly];

        return getMonthlyPriceWithOrWithoutDiscounts(monthlyPrice.withoutDiscounts, monthlyPrice.withDiscounts);
    },
);

const selectMonthlyEndTariffPrice = createAppSelector(
    selectTariffPrice,
    tariffPrice => {
        if (!tariffPrice) {
            return null;
        }

        const monthlyPrice = tariffPrice[PriceType.Monthly];

        return getMonthlyPriceWithOrWithoutDiscounts(
            monthlyPrice.withoutDiscounts,
            monthlyPrice.withDiscounts,
            getFromArrayWithRunTimeEnd,
        );
    },
);

const selectConnectionFee = createAppSelector(
    selectCustomerConfig,
    selectTariffPrice,
    (customerConfig, tariffPrice) => {
        if (!tariffPrice || customerConfig.isHardwareOnly) {
            return null;
        }

        const oneTimePrice = tariffPrice[PriceType.Onetime];

        const connectionFee = oneTimePrice.withoutDiscounts;

        return connectionFee?.gross ? connectionFee : null;
    },
);

const selectStairway = createAppSelector(
    selectMonthlyTariffPrice,
    selectMonthlyEndTariffPrice,
    (tariffPrice, endTariffPrice) => !!tariffPrice && !!endTariffPrice && tariffPrice !== endTariffPrice,
);

const selectStrikePrice = createAppSelector(
    selectCustomerConfig,
    selectSubscription,
    (customerConfig, activeOffer) => {
        if (!customerConfig.salesChannel || !activeOffer || customerConfig.paymentMethod !== PriceType.Monthly) {
            return null;
        }

        return activeOffer.priceMonthlyStrikePrice;
    },
);

const selectTechnicalDetails = createAppSelector(
    selectAtomicDevice,
    atomicDevice => {
        if (!atomicDevice) {
            return [];
        }

        const attributeGroupIdWhitelist = Object.keys(window[ADDITIONAL_PAGE_OPTIONS].technicalDetails.icons);

        return filterTechnicalDetails(attributeGroupIdWhitelist, atomicDevice.technicalDetails || null);
    },
);

const selectDeliveryScope = createAppSelector(
    selectAtomicDevice,
    atomicDevice => atomicDevice?.deliveryScope || [],
);

const selectTrackingPayload = createAppSelector(
    selectSubscription,
    selectAtomicDevice,
    selectCustomerConfig,
    selectMonthlyTariffPrice,
    selectConnectionFee,
    (subscription, atomic, customerConfig, priceToPay, connectionFee) => ({
        subscription,
        atomic,
        customerConfig,
        priceToPay,
        connectionFee,
    }),
);

const createStructuredAppSelector =
    createStructuredSelector.withTypes<RootState>();

export const mapStateToProps = createStructuredAppSelector({
    isLoading: selectLoading,
    customerConfig: selectCustomerConfig,
    currentCaseSize: selectCurrentCaseSize,
    currentColor: selectCurrentColor,
    device: selectDevice,
    availableColors: selectColorOptions,
    availableCaseSizes: selectCaseSizeOptions,
    cellular: selectCellular,
    atomicDevice: selectAtomicDevice,
    atomicImages: selectAtomicImages,
    caseSizesForColor: selectCaseSizesForColor,
    subscription: selectSubscription,
    hardwareOneTimePrice: selectHardwareOneTimePrice,
    hasStairway: selectStairway,
    strikePrice: selectStrikePrice,
    tariffPriceAtRuntimeStart: selectMonthlyTariffPrice,
    tariffPriceAtRuntimeEnd: selectMonthlyEndTariffPrice,
    connectionFee: selectConnectionFee,
    technicalDetails: selectTechnicalDetails,
    deliveryScope: selectDeliveryScope,
    hasError: selectHasError,
    activeAccordionPanel: selectActiveAccordionPanel,
});

export {
    selectLoading,
    selectCustomerConfig,
    selectCurrentColor,
    selectCurrentCaseSize,
    selectDevice,
    selectCellular,
    selectColorOptions,
    selectCaseSizeOptions,
    selectCaseSizesForColor,
    selectAtomicDevices,
    selectAtomicDevice,
    selectHardwareOneTimePrice,
    selectAtomicImages,
    selectSubscription,
    selectMonthlyTariffPrice,
    selectMonthlyEndTariffPrice,
    selectConnectionFee,
    selectStairway,
    selectStrikePrice,
    selectTechnicalDetails,
    selectDeliveryScope,
    selectTrackingPayload,
    selectHasError,
};
